import { t } from "../lib/i18n";
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getDashboardData, getPaymentsData } from '../api';
import QuickActionsSection from './QuickActionsSection';
import KPISection from './KPISection';
import UpcomingPaymentsSection from './UpcomingPaymentsSection';
import OverduePaymentsSection from './OverduePaymentsSection';
import LeaseExpirationChart from './LeaseExpirationChart';
import OccupancyChart from './OccupancyChart';
import PropertyTypeChart from './PropertyTypeChart';
import VacancyRateChart from './VacancyRateChart';
import { CalendarView } from './CalendarView'; // Import CalendarView
import SummaryReportSection, { type SummaryReport } from './SummaryReportSection'; // New import, including SummaryReport interface
import AIChatWindow from './AIChatWindow'; // Import AIChatWindow
import { User } from 'firebase/auth';
// Skeleton Components
const KPISkeleton = () => (
  <div className="bg-bg-secondary p-4 rounded shadow border border-border-primary animate-pulse">
    <div className="h-6 bg-gray-300 rounded w-3/4 mb-2"></div>
    <div className="h-10 bg-gray-300 rounded w-1/2"></div>
  </div>
);

const ChartSkeleton = () => (
  <div className="bg-bg-secondary p-4 rounded shadow border border-border-primary animate-pulse">
    <div className="h-8 bg-gray-300 rounded w-1/2 mb-4"></div>
    <div className="h-64 bg-gray-300 rounded"></div>
  </div>
);

const TableSkeleton = () => (
  <div className="bg-bg-secondary p-4 rounded shadow border border-border-primary animate-pulse">
    <div className="h-8 bg-gray-300 rounded w-1/3 mb-4"></div>
    <div className="space-y-2">
      <div className="h-4 bg-gray-300 rounded"></div>
      <div className="h-4 bg-gray-300 rounded"></div>
      <div className="h-4 bg-gray-300 rounded"></div>
      <div className="h-4 bg-gray-300 rounded"></div>
    </div>
  </div>
);

interface DashboardData {
  total_properties: number;
  total_tenants: number;
  total_leases: number;
  total_documents: number;
  average_monthly_rent: number;
  recent_activity: any[];
  lease_expirations_summary: any;
  occupancy_summary: any;
  property_type_summary: any;
  vacancy_rate_summary: any;
}

interface PaymentsData {
  upcoming_payments: any[];
  overdue_payments: any[];
}

export const Dashboard: React.FC<{ user: User }> = ({ user }) => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [paymentsData, setPaymentsData] = useState<PaymentsData | null>(null);
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const [paymentsError, setPaymentsError] = useState<string | null>(null);
  const [summaryReport, setSummaryReport] = useState<SummaryReport | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardAndSummaryData = async () => {
      setLoadingDashboard(true);
      setSummaryLoading(true);
      try {
        const data = await getDashboardData();
        setDashboardData(data);
        setSummaryReport({
          total_properties: data.total_properties,
          total_tenants: data.total_tenants,
          total_documents: data.total_documents,
          average_monthly_rent: data.average_monthly_rent,
        });
      } catch (err) {
        setDashboardError('Failed to fetch dashboard data.');
        setSummaryError('Failed to fetch summary report.');
        console.error(err);
      } finally {
        setLoadingDashboard(false);
        setSummaryLoading(false);
      }
    };

    fetchDashboardAndSummaryData();
  }, []);

  useEffect(() => {
    const fetchPayments = async () => {
      setLoadingPayments(true);
      try {
        const data = await getPaymentsData();
        console.log('Payments data:', data);
        setPaymentsData(data);
      } catch (err) {
        setPaymentsError('Failed to fetch payments data.');
        console.error(err);
      } finally {
        setLoadingPayments(false);
      }
    };

    fetchPayments();
  }, []);

  if (loadingDashboard || loadingPayments) {
    return (
      <div className="container mx-auto p-4 bg-bg-primary min-h-screen">
        <h1 className="text-2xl font-bold mb-4 text-text-secondary">{t("dashboard.k1")}</h1>
        <QuickActionsSection />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <KPISkeleton />
          <KPISkeleton />
          <KPISkeleton />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <ChartSkeleton />
          <ChartSkeleton />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <ChartSkeleton />
          <ChartSkeleton />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <TableSkeleton />
          <TableSkeleton />
        </div>
        <TableSkeleton />
      </div>
    );
  }

  if (dashboardError || paymentsError) {
    return <div className="container mx-auto p-4 text-danger">{t("dashboard.k2")} {dashboardError || paymentsError}</div>;
  }

  if (!dashboardData) {
    return <div className="container mx-auto p-4">{t("dashboard.k3")}</div>;
  }

  return (
    <div className="w-full px-4 py-4 bg-bg-primary min-h-screen">
      <AIChatWindow />
      <h1 className="text-2xl font-bold mb-4 text-text-secondary">{t("dashboard.k1")}</h1>

      <QuickActionsSection />

      {loadingDashboard ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <KPISkeleton />
          <KPISkeleton />
          <KPISkeleton />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <KPISection title={t("dashboard.k4")} value={dashboardData?.total_properties} link="/properties" />
          <KPISection title={t("dashboard.k5")} value={dashboardData?.total_tenants} link="/tenants" />
          <KPISection title={t("dashboard.k6")} value={dashboardData?.total_leases} link="/leases" />
        </div>
      )}

      <SummaryReportSection summary={summaryReport} loading={summaryLoading} error={summaryError} />
      <div className="mb-6"></div>

      {loadingDashboard ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <ChartSkeleton />
          <ChartSkeleton />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-bg-secondary p-4 rounded shadow border border-border-primary">
            <h2 className="text-xl font-bold mb-4 text-text-secondary">{t("dashboard.k7")}</h2>
            <LeaseExpirationChart data={dashboardData?.lease_expirations_summary} />
          </div>
          <div className="bg-bg-secondary p-4 rounded shadow border border-border-primary">
            <h2 className="text-xl font-bold mb-4 text-text-secondary">{t("dashboard.k8")}</h2>
            <OccupancyChart data={dashboardData?.occupancy_summary} />
          </div>
        </div>
      )}

      {loadingDashboard ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <ChartSkeleton />
          <ChartSkeleton />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-bg-secondary p-4 rounded shadow border border-border-primary">
            <h2 className="text-xl font-bold mb-4 text-text-secondary">{t("dashboard.k9")}</h2>
            <PropertyTypeChart data={dashboardData?.property_type_summary} />
          </div>
          <div className="bg-bg-secondary p-4 rounded shadow border border-border-primary">
            <h2 className="text-xl font-bold mb-4 text-text-secondary">{t("dashboard.k10")}</h2>
            <VacancyRateChart data={dashboardData?.vacancy_rate_summary} />
          </div>
        </div>
      )}

      {/* Calendar Section */}
      <div>
        <CalendarView />
      </div>
    </div>
  );
};


